import axios from 'axios';
import { API_CONFIG } from './config';

interface Nifty50LtpData {
  ltp: number;
  timestamp: number;
  isDataFresh: boolean;
  lastUpdateAgo: number;
  responseTime?: number;
}

interface Nifty50LtpUpdate {
  ltp: number;
  timestamp: number;
}

class Nifty50LtpService {
  private baseURL: string;
  private eventSource: EventSource | null = null;
  private updateCallbacks: ((data: Nifty50LtpUpdate) => void)[] = [];

  constructor() {
    this.baseURL = `${API_CONFIG.baseURL}/nifty50-ltp`;
  }

  private getAuthHeaders() {
    return API_CONFIG.getAuthHeaders();
  }

  // Get current NIFTY 50 LTP with metadata
  async getCurrentLtp(): Promise<Nifty50LtpData> {
    try {
      const response = await axios.get(
        `${this.baseURL}/current`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get current NIFTY 50 LTP');
    }
  }

  // Get instant NIFTY 50 LTP (similar to getStrikeLTPs pattern)
  async getInstantLtp(): Promise<number> {
    try {
      const response = await axios.get(
        `${this.baseURL}/instant`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.ltp;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get instant NIFTY 50 LTP');
    }
  }

  // Get instant LTP with response time info
  async getInstantLtpWithMetrics(): Promise<Nifty50LtpData & { responseTime: number }> {
    try {
      const startTime = Date.now();
      const response = await axios.get(
        `${this.baseURL}/instant`,
        { headers: this.getAuthHeaders() }
      );
      const clientResponseTime = Date.now() - startTime;

      return {
        ltp: response.data.ltp,
        timestamp: response.data.timestamp,
        responseTime: response.data.responseTime,
        clientResponseTime,
        isDataFresh: true,
        lastUpdateAgo: 0
      } as any;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get instant NIFTY 50 LTP');
    }
  }

  // Start real-time streaming (50ms updates)
  startRealTimeStream(onUpdate: (data: Nifty50LtpUpdate) => void, onError?: (error: Event) => void) {
    if (this.eventSource) {
      this.stopRealTimeStream();
    }

    const token = localStorage.getItem('token');
    const streamUrl = `${this.baseURL}/stream${token ? `?token=${token}` : ''}`;

    this.eventSource = new EventSource(streamUrl);

    this.eventSource.onopen = () => {
      console.log('[Nifty50LTP] Real-time stream connected');
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as Nifty50LtpUpdate;
        onUpdate(data);

        // Notify all registered callbacks
        this.updateCallbacks.forEach(callback => callback(data));
      } catch (error) {
        console.error('[Nifty50LTP] Error parsing stream data:', error);
      }
    };

    this.eventSource.onerror = (error) => {
      console.error('[Nifty50LTP] Stream error:', error);
      if (onError) onError(error);
    };

    return this.eventSource;
  }

  // Stop real-time streaming
  stopRealTimeStream() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      console.log('[Nifty50LTP] Real-time stream disconnected');
    }
  }

  // Subscribe to updates (for multiple components)
  onUpdate(callback: (data: Nifty50LtpUpdate) => void) {
    this.updateCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index > -1) {
        this.updateCallbacks.splice(index, 1);
      }
    };
  }

  // Utility method for periodic polling (alternative to streaming)
  startPolling(intervalMs: number = 50, onUpdate: (ltp: number) => void) {
    const pollInterval = setInterval(async () => {
      try {
        const ltp = await this.getInstantLtp();
        onUpdate(ltp);
      } catch (error) {
        console.error('[Nifty50LTP] Polling error:', error);
      }
    }, intervalMs);

    // Return stop function
    return () => clearInterval(pollInterval);
  }

  // Check if service is available
  async isServiceAvailable(): Promise<boolean> {
    try {
      await this.getCurrentLtp();
      return true;
    } catch (error) {
      return false;
    }
  }
}

export default new Nifty50LtpService();